<template>
  <n-input
   ref="input"
   v-bind="$attrs"
   type="search"
   :value="value"
   name="location-search"
   :autocomplete="$attrs.autocomplete || 'off'"
   @input="$emit('input', $event)"
   @focus="$event.currentTarget.select()">
    <n-button slot="append" v-show="geolocation" @click.native="findUserLocation" icon flat :title="geolocationHint">
      <n-progress-circular v-if="geolocationInProgress" indeterminate size="24"/>
      <n-icon v-else class="lumo-pink--text">my_location</n-icon>
      <slot name="geolocation-btn"></slot>
    </n-button>

    <div v-if="error" class="error white--text pa-2">
      <template v-if="error.code === 'ZERO_RESULTS'">{{noResultsError}}</template>
      <template v-if="error.code === 'PLACE_RESULT_NOT_SELECTED'">{{resultNotSelectedError}}</template>
      <template v-else-if="error.code === 'NON_STREET_ADDRESS'">{{nonStreetAddressError}}</template>
    </div>
    <slot name="label" slot="label" slot-scope="{label}" :label="label"></slot> <!-- forward label slot content to <n-input>'s label slot -->
    <slot name="prepend" slot="prepend"></slot> <!-- forward prepend slot content to <n-input>'s prepend slot -->
    <slot name="append" slot="append"></slot> <!-- forward prepend slot content to <n-input>'s prepend slot -->
    <slot name="help" slot="help"></slot> <!-- forward help slot content to <n-input>'s help slot -->
    <slot name="default" slot="default"></slot> <!-- forward default slot content to <n-input>'s default slot -->

  </n-input>
</template>

<script>
  import GoogleMapsApiLoader from '../../utils/google-maps-api-loader';
  import { getPlaceFromCoOrds, getPlaceFromAddress, convertPlaceToAddress, isValidStreetAddress } from '../../utils/google-maps-helpers';
  import { getPosition } from 'nandos-ordering/src/utils/get-user-location';

  export default {

    props: {
      value: { required: true },
      fullStreetAddressOnly: { type: Boolean, default: false },
      fallbackToGeoSearch: { type: Boolean, default: true },
      countryRestriction: { type: [String, Array] },
      geolocation: { type: Boolean, default: false },
      geolocationHint: { type: String, default: 'Use my current location' },
      noResultsError: { type: String, default: 'No results found' },
      resultNotSelectedError: { type: String, default: 'You must choose an item from the dropdown list.' },
      nonStreetAddressError: { type: String, default: 'The chicken doesn\'t know which road to cross. Please enter a valid street address (number, street, suburb).' },
    },

    data: function () {
      return {
        autocomplete: null,
        geolocationInProgress: false,
        place: null,
        error: null,
        resultFields: ['name', 'geometry', 'formatted_address', 'address_components'], // place result properties we require
      }
    },

    computed: {
      types() {
        return this.fullStreetAddressOnly ? ['address'] : []
      },

      componentRestrictions() {
        return {
          country: this.countryRestriction || [],
        }
      }
    },

    watch: {

      fullStreetAddressOnly() {
        if(this.autocomplete) this.autocomplete.setTypes(this.resultTypes)
      },

      // Whenever place changes, emit an updated address
      place() {
        let address = convertPlaceToAddress(this.place);

        if( this.fullStreetAddressOnly == false || isValidStreetAddress(address) ) {
          this.$emit('place-selected', address);
        }
        else {
          this.error = { code: 'NON_STREET_ADDRESS' }
        }

        // Emit an 'input' event to make sure any v-model associated with this component get updated
        this.$emit('input', address.formattedAddress);
      }
    },

    mounted() {
      GoogleMapsApiLoader.getApi()
        .then(() => this.setUpPlacesInputInstance())
    },

    methods: {

      setUpPlacesInputInstance() {
        let options = { componentRestrictions: this.componentRestrictions, types: this.types }
        let targetEl = this.$el.querySelector('input')
        let targetElOriginalPlaceholder = targetEl.placeholder
        this.autocomplete = new google.maps.places.Autocomplete(targetEl, options);
        targetEl.placeholder = targetElOriginalPlaceholder // prevent google from setting their placeholder 
        this.autocomplete.setFields(this.resultFields)
        this.autocomplete.addListener('place_changed', () => {
          this.error = null;
          let result = this.autocomplete.getPlace();
          if(result.geometry) {
            this.place = result;
          }
          else {
            if(this.fallbackToGeoSearch) {
              this.geolocationInProgress = true
              // User pressed enter, use the existing search string to try and find a place
              getPlaceFromAddress(this.value, this.componentRestrictions)
                .then(result => {
                  if(result && result.geometry) this.place = result
                })
                .catch(e => {
                  this.$emit('place-error', e)
                  this.error = { code: e }
                })
                .finally(() => {
                  this.geolocationInProgress = false
                  this.$refs.input && this.$refs.input.blur()
                })
            }
            else{
              this.error = { code: 'PLACE_RESULT_NOT_SELECTED' }
            }
          }
        });
      },

      findUserLocation() {
        this.geolocationInProgress = true;
        getPosition({timeout: 10000})
          .catch(error => this.$emit('geolocation-error', {code: 'USER_DENIED_GEOLOCATION', message: error.message}))
          .then(coords => getPlaceFromCoOrds(coords.latitude, coords.longitude/*, this.componentRestrictions*/))
          .then(result => this.place = result)
          .finally(() => this.geolocationInProgress = false)          
      },

      focus() {
        this.$refs['input'].focus()
      },
    },

    beforeDestroy() {
      if(this.autocomplete) {
        this.autocomplete.unbindAll();
        google.maps.event.clearInstanceListeners(this.autocomplete);
      }
    }
  }
</script>